/**
 * UO交易大厅 — bottom tab default for ORB 交易 (ys142857 parity).
 */
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { otcHallInfiniteQueryOptions } from '../hooks/prefetchOtcHall';
import { keepPreviousData, useInfiniteQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useUiStore } from '../store/ui';
import { EmptyState } from '../components/ui/EmptyState';
import { LoadingSkeleton } from '../components/ui/LoadingSkeleton';
import { ApiError } from '../api/client';
import {
  UoHallCard,
  mapUoHallRowDisplay,
  type UoHallRow,
} from '../components/trading/UoHallCard';
import { MenuIcon } from '../components/wallet/WalletShared';
import { OTC_COLORS as C } from '@/constants';

export function UoTradingHall() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const openDrawer = useUiStore((s) => s.openDrawer);
  const loadMoreRef = useRef<HTMLDivElement | null>(null);

  const hallQuery = useInfiniteQuery({
    ...otcHallInfiniteQueryOptions(),
    placeholderData: keepPreviousData,
  });

  const statusFallback = t('common.tradeDone', '交易完成');
  const labels = useMemo(
    () => ({
      sellTotal: t('common.sellTotal', '挂卖总数'),
      filled: t('common.filled', '已成交'),
      coinPrice: t('uoTradingHall.coinPrice', '币价'),
      value: t('common.value', '价值'),
    }),
    [t],
  );

  const rows = useMemo(
    () =>
      (hallQuery.data?.pages.flatMap((p) => p.rows ?? []) ?? []).map((row) =>
        mapUoHallRowDisplay(row as UoHallRow, statusFallback),
      ),
    [hallQuery.data?.pages, statusFallback],
  );

  const hasMore = hallQuery.hasNextPage ?? false;
  const showInitialSkeleton = hallQuery.isLoading && rows.length === 0;
  const showError = hallQuery.isError && rows.length === 0;

  const onOpenHall = useCallback(
    (routeId: string) => {
      navigate(`/trading/hall/${routeId}`);
    },
    [navigate],
  );

  useEffect(() => {
    const el = loadMoreRef.current;
    if (!el || !hasMore || hallQuery.isFetchingNextPage) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && hallQuery.hasNextPage && !hallQuery.isFetchingNextPage) {
          void hallQuery.fetchNextPage();
        }
      },
      { rootMargin: '240px' },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [hasMore, hallQuery.isFetchingNextPage, hallQuery.fetchNextPage, hallQuery.hasNextPage]);

  return (
    <div className="relative min-h-full" style={{ background: C.pageBg }}>
      <div className="relative mx-auto min-h-full w-full max-w-[430px]">
        <header
          className="sticky top-0 z-50 flex h-[56px] w-full items-center justify-between px-5"
          style={{ background: C.topbarBg }}
        >
          <button
            type="button"
            aria-label={t('common.menu', '菜单')}
            onClick={openDrawer}
            className="flex size-[30px] items-center justify-center"
            style={{ color: C.text }}
          >
            <MenuIcon />
          </button>
          <span
            className="bg-clip-text text-base font-semibold text-transparent"
            style={{ backgroundImage: C.titleGradient }}
          >
            {t('uoTradingHall.title', 'UO交易大厅')}
          </span>
          <button
            type="button"
            onClick={() => navigate('/trading/orders')}
            className="text-xs font-semibold"
            style={{ color: C.gold }}
          >
            {t('uoHub.myOrders', '我的买卖')}
          </button>
        </header>

        <div className="pb-4">
          {showInitialSkeleton ? (
            <div className="flex flex-col gap-2 px-2 pt-2">
              <LoadingSkeleton className="h-28" />
              <LoadingSkeleton className="h-28" />
              <LoadingSkeleton className="h-28" />
            </div>
          ) : showError ? (
            <EmptyState
              title={
                hallQuery.error instanceof ApiError
                  ? hallQuery.error.message
                  : t('common.loadFailed', '加载失败')
              }
            />
          ) : rows.length === 0 ? (
            <EmptyState title={t('otc.hallEmpty', '暂无大厅成交卡片')} />
          ) : (
            <>
              {rows.map((row) => (
                <UoHallCard key={row.id} row={row} labels={labels} onOpen={onOpenHall} />
              ))}
              {hasMore ? (
                <div ref={loadMoreRef} className="flex justify-center px-4 py-3">
                  <span className="text-xs" style={{ color: C.muted }}>
                    {hallQuery.isFetchingNextPage
                      ? t('common.loading', '加载中…')
                      : t('common.more', '更多')}
                  </span>
                </div>
              ) : null}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
